/**
 * The storage-health line, and the footer it lives in.
 *
 * The save archive is where a player already goes to think about losing things,
 * so it is where the durability facts belong: whether the browser promised to
 * keep our data, how much of it there is, and when it was last backed up. One
 * line, no colour unless something is actually wrong.
 *
 * The footer element is created here rather than in index.html because another
 * branch holds that file; the backup controls (commit 2) hang off the same
 * container.
 *
 * TODO: move #rr-saves-footer into the #overlay-saves markup once the tree quiets.
 */

import { storageHealth } from '../durability';
import { $, bytes } from './dom';

export interface StorageHealthPanel {
  /** The footer the backup controls also mount into. */
  readonly footer: HTMLElement;
  refresh(): void;
}

const fmtWhen = (at: number | null): string => {
  if (!at) return 'never';
  const days = Math.floor((Date.now() - at) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

export function mountStorageHealth(): StorageHealthPanel {
  const body = $('#overlay-saves .body');

  const footer = document.createElement('div');
  footer.id = 'rr-saves-footer';

  const line = document.createElement('p');
  line.className = 'hint';
  line.id = 'rr-storage-health';
  line.textContent = 'Storage — checking…';
  footer.appendChild(line);
  body.appendChild(footer);

  const refresh = () => {
    void storageHealth().then((health) => {
      const parts: string[] = [];
      parts.push(
        health.persisted === null
          ? 'durability unknown'
          : health.persisted
            ? 'marked persistent'
            : 'not marked persistent'
      );
      if (health.usage !== null) {
        parts.push(health.quota ? `${bytes(health.usage)} of ${bytes(health.quota)} used` : `${bytes(health.usage)} used`);
      }
      parts.push(`last backup ${fmtWhen(health.lastBackupAt)}`);
      if (health.destination) parts.push(`backing up to ${health.destination}`);
      line.textContent = `Storage — ${parts.join(' · ')}.`;
      // "Not persistent" is a fact, not an emergency: the browser may still
      // keep everything for years. It gets a colour only because it is the one
      // state where a backup is worth taking today rather than eventually.
      line.classList.toggle('warn', health.persisted === false || health.lastBackupAt === null);
    });
  };

  refresh();
  return { footer, refresh };
}
